import { getStatusHistory } from './status-history.helper';

/**
 * Whether the client signed off on a record an admin raised for them:
 * - `skipped`   the admin raised it with `requireConfirmation: false` (or skipConfirmation on a
 *               withdrawal), so the client was never asked;
 * - `confirmed` the admin raised it with confirmation and the client accepted;
 * - `null`      the client raised it themselves, or it is still waiting / was declined -- the
 *               status tag already says which.
 */
export type ClientConfirmation = 'skipped' | 'confirmed' | null;

export type ConfirmationRowKind = 'order' | 'ledger';

/**
 * Stock, FX and OTC orders keep the marker in `extra`. Stock and FX mark admin-raised orders with
 * `extra.createdByAdmin`; OTC has only `extra.adminId`. The client's acceptance is the
 * `pending_confirmation -> *` transition they made themselves (a decline lands in `cancelled`).
 */
function getOrderConfirmation(order: any): ClientConfirmation {
  const extra = order?.extra;
  if (!extra) return null;
  if (extra.confirmationSkipped === true) return 'skipped';
  if (!extra.createdByAdmin && !extra.adminId) return null;

  const accepted = getStatusHistory(order).some(
    (entry) =>
      entry.from === 'pending_confirmation' &&
      entry.source === 'user' &&
      entry.to !== 'cancelled'
  );
  return accepted ? 'confirmed' : null;
}

/**
 * Ledger rows carry the marker top-level (`extra` is a plain string there). `createdBy` is the
 * staff member who raised it. Ledger rows have no status history, so "confirmed" is read from the
 * status: `new` is still waiting for the client's code, `cancelled` declined or rolled back.
 * External-order import legs set `createdBy` without asking anyone and are `source: 'trade'`.
 */
function getLedgerConfirmation(row: any): ClientConfirmation {
  if (!row) return null;
  if (row.confirmationSkipped === true) return 'skipped';
  if (!row.createdBy || row.source === 'trade') return null;
  return row.status === 'new' || row.status === 'cancelled'
    ? null
    : 'confirmed';
}

export function getClientConfirmation(
  row: any,
  kind: ConfirmationRowKind
): ClientConfirmation {
  return kind === 'ledger'
    ? getLedgerConfirmation(row)
    : getOrderConfirmation(row);
}
